import { Controller } from 'stimulus'
import MatrixSupportEditor from 'src/lane_matrix/support_editor'

const POLL_INTERVAL_MS = 3000
const PROFILE_REVIEW_FIELD_FALLBACKS = {
  commodity_length: { raw: 'commodity_length_raw', canonical: 'commodity_length_in', unit: 'in' },
  commodity_width: { raw: 'commodity_width_raw', canonical: 'commodity_width_in', unit: 'in' },
  commodity_height: { raw: 'commodity_height_raw', canonical: 'commodity_height_in', unit: 'in' },
  commodity_weight: {
    raw: 'commodity_weight_raw',
    canonical: 'commodity_weight_lb',
    unit: 'lb',
    preferCanonical: true,
    maximumFractionDigits: 0,
  },
}
const PROFILE_REVIEW_FIELDS = [
  ['label', 'Load'],
  ['commodity_length', 'Length'],
  ['commodity_width', 'Width'],
  ['commodity_height', 'Height'],
  ['commodity_weight', 'Weight'],
]

export default class extends Controller {
  static targets = [
    'plan',
    'profileRows',
    'originBlocks',
    'filterInput',
    'laneCount',
    'loadCount',
    'scenarioCount',
    'destinationSummary',
    'status',
    'confirmButton',
    'supportEditor',
    'supportReadiness',
  ]

  static values = {
    confirmUrl: String,
    statusUrl: String,
    processing: Boolean,
    setupUrl: String,
    projectEdit: Boolean,
    projectUrl: String,
  }

  connect() {
    this.pollTimer = null
    this.terminalHandled = false

    try {
      this.plan = JSON.parse(this.planTarget.textContent || '{}')
    } catch (_error) {
      this.showStatus('The matrix proposal could not be displayed. Reload the page and try again.', 'danger')
      return
    }

    this.lanes = Array.isArray(this.plan.lanes) ? this.plan.lanes : []
    this.loadProfiles = Array.isArray(this.plan.load_profiles) ? this.plan.load_profiles : []
    this.lanesByKey = new Map(this.lanes.map((lane) => [String(lane.key), lane]))
    this.profilesByKey = new Map(this.loadProfiles.map((profile) => [String(profile.key), profile]))

    const saved = this.plan.selection && typeof this.plan.selection === 'object' ? this.plan.selection : {}
    this.selectedLaneKeys = new Set(
      Array.isArray(saved.selected_lane_keys)
        ? saved.selected_lane_keys.map(String)
        : this.lanes.map((lane) => String(lane.key))
    )
    this.selectedLoadProfileKeys = new Set(
      Array.isArray(saved.selected_load_profile_keys)
        ? saved.selected_load_profile_keys.map(String)
        : this.loadProfiles
          .filter((profile) => profile.selected_by_default !== false)
          .map((profile) => String(profile.key))
    )
    this.excludedCellPairs = new Map()
    this.unmappedExcludedCellKeys = []
    this.restoreExcludedCells(saved.excluded_cell_keys)
    this.laneOverrides = this.deepClone(saved.lane_overrides || {})
    this.loadProfileOverrides = this.deepClone(saved.load_profile_overrides || {})
    this.setupVersion = Number(this.plan.setup_version || 0)
    this.supportEditor = new MatrixSupportEditor(this)

    this.originGroups = this.buildOriginGroups()
    this.renderProfiles()
    this.renderOriginBlocks()
    this.updateCounts()
    this.syncProcessingState()
    const requestedProfile = this.profilesByKey.get(this.plan.initial_profile_key)
    const initial = requestedProfile ? { profile: requestedProfile, index: 0 } : this.supportEditor.pending()[0] || { profile: this.loadProfiles[0], index: 0 }
    if (initial.profile) this.supportEditor.open(initial.profile, initial.index)

    if (this.processingValue) this.startPolling()
  }

  disconnect() {
    this.stopPolling()
    this.supportEditor?.disconnect()
  }

  buildOriginGroups() {
    const groups = new Map()
    this.lanes.forEach((lane) => {
      const key = this.originGroupKey(lane)
      if (!groups.has(key)) groups.set(key, { key, lanes: [] })
      groups.get(key).lanes.push(lane)
    })
    return Array.from(groups.values())
  }

  originGroupKey(lane) {
    const metadataKey = lane.metadata && (lane.metadata.origin_key || lane.metadata.origin_id)
    return String(lane.origin_key || metadataKey || lane.origin || 'origin')
  }

  laneState(lane) {
    const componentState = lane.destination_components && lane.destination_components.state
    if (componentState) return String(componentState).toUpperCase()
    const match = String(lane.destination || '')
      .match(/,\s*([A-Za-z]{2})(?:\s+(?:\d{5}(?:-\d{4})?|[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d))?\s*$/)
    return match ? match[1].toUpperCase() : 'Other'
  }

  laneCityLabel(lane, state) {
    const destination = String(lane.destination || '').trim()
    if (state === 'Other') return destination || 'Destination'
    const stateToken = new RegExp(`,\\s*${state}\\b`, 'i')
    return destination.replace(stateToken, '').replace(/\s{2,}/g, ' ').trim() || destination
  }

  renderProfiles() {
    this.profileRowsTarget.replaceChildren()
    this.loadProfiles.forEach((profile) => {
      const key = String(profile.key)
      const row = document.createElement('tr')
      row.dataset.profileKey = key
      if (!this.selectedLoadProfileKeys.has(key)) row.classList.add('is-unselected')

      const checkCell = this.elementFor('td', 'lane-matrix-review__check-cell')
      const checkbox = this.checkbox(`Include ${profile.label || key}`, this.selectedLoadProfileKeys.has(key))
      checkbox.dataset.profileKey = key
      checkbox.disabled = this.processingValue || this.projectEditValue
      checkbox.addEventListener('change', (event) => this.toggleProfile(event))
      checkCell.appendChild(checkbox)
      row.appendChild(checkCell)

      PROFILE_REVIEW_FIELDS.forEach(([fieldName, fieldLabel]) => {
        const cell = this.elementFor('td', fieldName === 'label' ? 'lane-matrix-review__name-cell' : '')
        const value = fieldName === 'label'
          ? this.effectiveProfileLabel(profile)
          : this.profileReviewFieldValue(profile, fieldName)
        const input = this.inlineInput(value, fieldLabel)
        input.readOnly = this.projectEditValue || (Array.isArray(profile.shipments) && profile.shipments.length > 1 && fieldName !== 'label')
        input.dataset.profileKey = key
        input.dataset.profileField = fieldName
        input.addEventListener('input', (event) => this.editProfile(event))
        cell.appendChild(input)
        if (fieldName === 'label' && Array.isArray(profile.shipments) && profile.shipments.length > 1) {
          cell.appendChild(this.elementFor('small', 'text-muted lane-matrix-review__component-count', `${profile.shipments.length} components`))
        }
        row.appendChild(cell)
      })
      row.appendChild(this.supportEditor.createCell(profile))
      this.profileRowsTarget.appendChild(row)

      const profileWarnings = Array.isArray(profile.warnings) ? profile.warnings.filter(Boolean) : []
      if (Array.isArray(profile.lane_keys) && profile.lane_keys.length < this.lanes.length) {
        const origins = [...new Set(profile.lane_keys.map((laneKey) => {
          const lane = this.lanesByKey.get(String(laneKey))
          return lane && lane.origin
        }).filter(Boolean))]
        profileWarnings.push(`Applies to ${this.pluralize(profile.lane_keys.length, 'lane')} from ${origins.join(', ')}.`)
      }
      if (profile.reason) profileWarnings.unshift(profile.reason)
      if (profileWarnings.length) {
        const warningRow = document.createElement('tr')
        warningRow.className = 'lane-matrix-review__profile-warning-row'
        const cell = this.elementFor('td', 'lane-matrix-review__profile-warning', profileWarnings.join(' · '))
        cell.colSpan = PROFILE_REVIEW_FIELDS.length + 2
        warningRow.appendChild(cell)
        this.profileRowsTarget.appendChild(warningRow)
      }
    })
  }

  renderOriginBlocks() {
    this.originBlocksTarget.replaceChildren()
    this.originGroups.forEach((group) => {
      const block = this.elementFor('div', 'lane-matrix-review__origin-block')
      block.dataset.originKey = group.key

      const head = this.elementFor('div', 'lane-matrix-review__origin-head')
      const originToggle = this.checkbox('Include origin', group.lanes.some((lane) => this.selectedLaneKeys.has(String(lane.key))))
      originToggle.dataset.originKey = group.key
      originToggle.dataset.role = 'origin-toggle'
      originToggle.addEventListener('change', (event) => this.toggleOrigin(event))
      head.appendChild(originToggle)
      head.appendChild(this.elementFor('span', 'lane-matrix-review__origin-label', 'Origin'))

      const originField = this.inlineInput(this.effectiveLaneValue(group.lanes[0], 'origin'), 'Origin')
      originField.classList.add('lane-matrix-review__origin-name')
      originField.dataset.originKey = group.key
      originField.addEventListener('input', (event) => this.editOrigin(event))
      head.appendChild(originField)

      const count = this.elementFor('span', 'lane-matrix-review__origin-count')
      const selectedCount = this.elementFor('span', '', '0')
      selectedCount.dataset.role = 'origin-selected-count'
      count.appendChild(selectedCount)
      count.appendChild(document.createTextNode(` of ${group.lanes.length.toLocaleString()} destinations`))
      head.appendChild(count)
      block.appendChild(head)

      const stateGroups = this.elementFor('div', 'lane-matrix-review__state-groups')
      const groupedLanes = this.stateGroupsFor(group)
      groupedLanes.forEach(([state, lanes]) => {
        stateGroups.appendChild(this.renderStateGroup(state, lanes, { headerless: groupedLanes.length === 1 }))
      })
      block.appendChild(stateGroups)
      this.originBlocksTarget.appendChild(block)
      this.updateGroupCheckboxes(block)
    })
  }

  stateGroupsFor(group) {
    const states = new Map()
    group.lanes.forEach((lane) => {
      const state = this.laneState(lane)
      if (!states.has(state)) states.set(state, [])
      states.get(state).push(lane)
    })
    return Array.from(states.entries()).sort(([a], [b]) => a.localeCompare(b))
  }

  renderStateGroup(state, lanes, { headerless = false } = {}) {
    const group = this.elementFor('div', 'lane-matrix-review__state-group')
    if (headerless) {
      // A single group (one state, or nothing parseable) flows freely across
      // the columns; its header would only duplicate the origin toggle.
      group.classList.add('lane-matrix-review__state-group--flow')
    } else {
      const head = this.elementFor('div', 'lane-matrix-review__state-head')
      const toggle = this.checkbox(`Include all ${state}`, lanes.some((lane) => this.selectedLaneKeys.has(String(lane.key))))
      toggle.dataset.role = 'state-toggle'
      toggle.addEventListener('change', (event) => this.toggleState(event))
      head.appendChild(toggle)
      head.appendChild(this.elementFor('span', '', state))
      group.appendChild(head)
    }

    lanes.forEach((lane) => {
      const laneKey = String(lane.key)
      const row = this.elementFor('div', 'lane-matrix-review__dest-row')
      row.dataset.laneKey = laneKey
      if (!this.selectedLaneKeys.has(laneKey)) row.classList.add('is-off')

      const city = this.laneCityLabel(lane, state)
      row.dataset.search = `${String(lane.destination || '').toLowerCase()} ${state.toLowerCase()}`

      const checkbox = this.checkbox(`Include ${lane.destination || 'lane'}`, this.selectedLaneKeys.has(laneKey))
      checkbox.dataset.laneKey = laneKey
      checkbox.id = `lane-matrix-lane-${laneKey}`
      checkbox.addEventListener('change', (event) => this.toggleLane(event))
      row.appendChild(checkbox)

      const label = this.elementFor('label', '', city)
      label.setAttribute('for', checkbox.id)
      row.appendChild(label)
      group.appendChild(row)
    })
    return group
  }

  toggleProfile(event) {
    this.setMembership(this.selectedLoadProfileKeys, event.currentTarget.dataset.profileKey, event.currentTarget.checked)
    const row = event.currentTarget.closest('tr')
    if (row) row.classList.toggle('is-unselected', !event.currentTarget.checked)
    this.updateCounts()
  }

  toggleLane(event) {
    const row = event.currentTarget.closest('.lane-matrix-review__dest-row')
    this.setLaneSelected(row, event.currentTarget.dataset.laneKey, event.currentTarget.checked)
    const block = event.currentTarget.closest('.lane-matrix-review__origin-block')
    if (block) this.updateGroupCheckboxes(block)
    this.updateCounts()
  }

  toggleState(event) {
    const group = event.currentTarget.closest('.lane-matrix-review__state-group')
    if (!group) return
    this.setRowsSelected(group.querySelectorAll('.lane-matrix-review__dest-row'), event.currentTarget.checked)
    const block = event.currentTarget.closest('.lane-matrix-review__origin-block')
    if (block) this.updateGroupCheckboxes(block)
    this.updateCounts()
  }

  toggleOrigin(event) {
    const block = event.currentTarget.closest('.lane-matrix-review__origin-block')
    if (!block) return
    this.setRowsSelected(block.querySelectorAll('.lane-matrix-review__dest-row'), event.currentTarget.checked)
    this.updateGroupCheckboxes(block)
    this.updateCounts()
  }

  selectAllLanes(event) {
    event.preventDefault()
    this.setAllLanes(true)
  }

  clearAllLanes(event) {
    event.preventDefault()
    this.setAllLanes(false)
  }

  setAllLanes(selected) {
    if (this.processingValue) return
    this.originBlocksTarget.querySelectorAll('.lane-matrix-review__origin-block').forEach((block) => {
      this.setRowsSelected(block.querySelectorAll('.lane-matrix-review__dest-row'), selected)
      this.updateGroupCheckboxes(block)
    })
    this.updateCounts()
  }

  setRowsSelected(rows, selected) {
    rows.forEach((row) => {
      const checkbox = row.querySelector('input[type="checkbox"]')
      if (checkbox) checkbox.checked = selected
      this.setLaneSelected(row, row.dataset.laneKey, selected)
    })
  }

  setLaneSelected(row, laneKey, selected) {
    this.setMembership(this.selectedLaneKeys, laneKey, selected)
    if (row) row.classList.toggle('is-off', !selected)
  }

  updateGroupCheckboxes(block) {
    block.querySelectorAll('.lane-matrix-review__state-group').forEach((group) => {
      const rows = group.querySelectorAll('.lane-matrix-review__dest-row input[type="checkbox"]')
      const checked = group.querySelectorAll('.lane-matrix-review__dest-row input[type="checkbox"]:checked')
      const toggle = group.querySelector('input[data-role="state-toggle"]')
      if (!toggle) return
      toggle.checked = rows.length > 0 && checked.length === rows.length
      toggle.indeterminate = checked.length > 0 && checked.length < rows.length
    })
    const rows = block.querySelectorAll('.lane-matrix-review__dest-row input[type="checkbox"]')
    const checked = block.querySelectorAll('.lane-matrix-review__dest-row input[type="checkbox"]:checked')
    const toggle = block.querySelector('input[data-role="origin-toggle"]')
    if (toggle) {
      toggle.checked = rows.length > 0 && checked.length === rows.length
      toggle.indeterminate = checked.length > 0 && checked.length < rows.length
    }
    const count = block.querySelector('[data-role="origin-selected-count"]')
    if (count) count.textContent = checked.length.toLocaleString()
  }

  filterDestinations() {
    const query = String(this.filterInputTarget.value || '').trim().toLowerCase()
    this.originBlocksTarget.querySelectorAll('.lane-matrix-review__state-group').forEach((group) => {
      let visible = 0
      group.querySelectorAll('.lane-matrix-review__dest-row').forEach((row) => {
        const match = !query || (row.dataset.search || '').includes(query)
        row.hidden = !match
        if (match) visible += 1
      })
      group.hidden = visible === 0
    })
  }

  editOrigin(event) {
    const group = this.originGroups.find((item) => item.key === event.currentTarget.dataset.originKey)
    if (!group) return
    group.lanes.forEach((lane) => this.setLaneOverride(String(lane.key), 'origin', event.currentTarget.value, lane.origin))
  }

  editProfile(event) {
    const key = event.currentTarget.dataset.profileKey
    const field = event.currentTarget.dataset.profileField
    const profile = this.profilesByKey.get(key)
    if (!profile) return
    const original = field === 'label'
      ? profile.label
      : this.profileReviewFieldValue(profile, field, { includeOverride: false })
    this.setProfileOverride(key, field, event.currentTarget.value, original)
    this.supportEditor?.profileChanged(profile)
  }

  setLaneOverride(key, field, value, original) {
    const normalized = String(value || '').trim()
    const originalValue = String(original || '').trim()
    const override = this.laneOverrides[key] || {}
    if (normalized === originalValue) delete override[field]
    else override[field] = normalized
    if (Object.keys(override).length) this.laneOverrides[key] = override
    else delete this.laneOverrides[key]
  }

  setProfileOverride(key, field, value, original) {
    const normalized = String(value || '').trim()
    const originalValue = String(original || '').trim()
    const override = this.loadProfileOverrides[key] || {}
    if (normalized === originalValue) delete override[field]
    else override[field] = normalized
    if (Object.keys(override).length) this.loadProfileOverrides[key] = override
    else delete this.loadProfileOverrides[key]
  }

  effectiveLaneValue(lane, field) {
    const override = this.laneOverrides[String(lane.key)] || {}
    if (Object.prototype.hasOwnProperty.call(override, field)) return override[field]
    return lane[field] || ''
  }

  effectiveProfileLabel(profile) {
    const override = this.loadProfileOverrides[String(profile.key)] || {}
    if (Object.prototype.hasOwnProperty.call(override, 'label')) return override.label
    return profile.label || String(profile.key)
  }

  profileShipment(profile) {
    if (profile.shipment && typeof profile.shipment === 'object') return profile.shipment
    if (Array.isArray(profile.shipments) && profile.shipments.length) return profile.shipments[0]
    return {}
  }

  profileReviewFieldValue(profile, fieldName, { includeOverride = true } = {}) {
    const key = String(profile.key)
    const override = (this.loadProfileOverrides && this.loadProfileOverrides[key]) || {}
    if (includeOverride && Object.prototype.hasOwnProperty.call(override, fieldName)) return override[fieldName]

    const shipment = this.profileShipment(profile)
    const fallback = PROFILE_REVIEW_FIELD_FALLBACKS[fieldName]
    if (fallback) {
      const rawValue = shipment[fallback.raw]
      const canonicalValue = shipment[fallback.canonical]
      if (!fallback.preferCanonical && this.reviewFieldValuePresent(rawValue)) return rawValue

      if (this.reviewFieldValuePresent(canonicalValue)) {
        return this.reviewFieldValueWithUnit(
          canonicalValue,
          fallback.unit,
          fallback.maximumFractionDigits
        )
      }

      if (this.reviewFieldValuePresent(rawValue)) return rawValue
    }

    return shipment[fieldName] ?? ''
  }

  reviewFieldValuePresent(value) {
    return value !== null && value !== undefined && String(value).trim() !== ''
  }

  reviewFieldValueWithUnit(value, unit, maximumFractionDigits = 3) {
    const numeric = Number(value)
    if (Number.isFinite(numeric)) {
      return `${numeric.toLocaleString('en-US', { maximumFractionDigits })} ${unit}`
    }

    const text = String(value).trim()
    return text.toLowerCase().endsWith(` ${unit}`) ? text : `${text} ${unit}`
  }

  restoreExcludedCells(values) {
    ;(Array.isArray(values) ? values : []).forEach((value) => {
      if (value && typeof value === 'object' && value.lane_key && value.load_profile_key) {
        const pair = { lane_key: String(value.lane_key), load_profile_key: String(value.load_profile_key) }
        this.excludedCellPairs.set(this.cellPairKey(pair.lane_key, pair.load_profile_key), pair)
      } else if (value) {
        this.unmappedExcludedCellKeys.push(String(value))
      }
    })
  }

  cellPairKey(laneKey, profileKey) {
    return JSON.stringify([String(laneKey), String(profileKey)])
  }

  scenarioCount() {
    let count = 0
    this.selectedLoadProfileKeys.forEach((key) => {
      const profile = this.profilesByKey.get(key)
      count += Array.isArray(profile.lane_keys)
        ? profile.lane_keys.filter((laneKey) => this.selectedLaneKeys.has(String(laneKey))).length
        : this.selectedLaneKeys.size
    })
    this.excludedCellPairs.forEach((pair) => {
      const profile = this.profilesByKey.get(pair.load_profile_key)
      const allowed = profile && (!Array.isArray(profile.lane_keys) || profile.lane_keys.includes(pair.lane_key))
      if (allowed && this.selectedLaneKeys.has(pair.lane_key) && this.selectedLoadProfileKeys.has(pair.load_profile_key)) count -= 1
    })
    return Math.max(count - this.unmappedExcludedCellKeys.length, 0)
  }

  selectionPayload() {
    return {
      plan_digest: String(this.plan.digest || ''),
      selected_lane_keys: Array.from(this.selectedLaneKeys),
      selected_load_profile_keys: Array.from(this.selectedLoadProfileKeys),
      excluded_cell_keys: [...Array.from(this.excludedCellPairs.values()), ...this.unmappedExcludedCellKeys],
      lane_overrides: this.laneOverrides,
      load_profile_overrides: this.loadProfileOverrides,
    }
  }

  updateCounts() {
    this.laneCountTarget.textContent = this.pluralize(this.selectedLaneKeys.size, 'lane')
    this.loadCountTarget.textContent = this.pluralize(this.selectedLoadProfileKeys.size, 'load type')
    this.scenarioCountTarget.textContent = this.pluralize(this.scenarioCount(), 'combination')
    if (this.hasDestinationSummaryTarget) {
      this.destinationSummaryTarget.textContent =
        `${this.selectedLaneKeys.size.toLocaleString()} of ${this.lanes.length.toLocaleString()} destinations selected`
    }
    this.confirmButtonTarget.disabled = this.processingValue || this.supportSaving || this.scenarioCount() <= 0
    this.supportEditor?.update()
  }

  async confirm(event) {
    event.preventDefault()
    if (this.scenarioCount() <= 0 || this.processingValue || this.supportSaving) return
    if (this.supportEditor?.focusPending()) return
    if (this.projectEditValue) {
      window.location = this.projectUrlValue
      return
    }
    await this.submitDecision(this.confirmUrlValue, {
      matrix_selection_json: JSON.stringify(this.selectionPayload()),
      matrix_setup_version: this.setupVersion,
    })
  }

  async saveSetup() {
    this.supportSaving = true
    this.syncProcessingState()
    try {
      const response = await fetch(this.setupUrlValue, {
        method: 'PATCH', credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-CSRF-Token': this.csrfToken() },
        body: JSON.stringify({ matrix_selection_json: JSON.stringify(this.selectionPayload()), matrix_setup_version: this.setupVersion }),
      })
      const payload = await response.json()
      if (!response.ok) throw new Error(payload.error || 'Could not save load setup.')
      this.setupVersion = payload.setup_version
    } finally {
      this.supportSaving = false
      this.syncProcessingState()
    }
  }

  async submitDecision(url, payload) {
    this.setProcessing(true)
    this.showStatus('Creating your project…', 'info')
    try {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken(),
        },
        body: JSON.stringify(payload),
      })
      const responsePayload = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(responsePayload.error || 'Could not start the import.')
      if (responsePayload.status_url) this.statusUrlValue = responsePayload.status_url
      this.terminalHandled = false
      this.showStatus('Creating your project… This may take a moment for large matrices.', 'info')
      this.startPolling()
    } catch (error) {
      this.setProcessing(false)
      this.showStatus(error.message || 'Could not start the import.', 'danger')
    }
  }

  startPolling() {
    this.stopPolling()
    this.fetchStatus()
    this.pollTimer = window.setInterval(() => this.fetchStatus(), POLL_INTERVAL_MS)
  }

  stopPolling() {
    if (this.pollTimer) window.clearInterval(this.pollTimer)
    this.pollTimer = null
  }

  async fetchStatus() {
    if (!this.statusUrlValue) return
    try {
      const response = await fetch(this.statusUrlValue, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
      if (!response.ok) return
      const payload = await response.json()
      if (payload.terminal === true) this.handleTerminal(payload)
    } catch (_error) {
      // A temporary network failure should not restart or cancel the import.
    }
  }

  handleTerminal(payload) {
    if (this.terminalHandled) return
    const result = payload.result || {}
    if (payload.status === 'processed' && result.status === 'project_created' && result.project_url) {
      this.terminalHandled = true
      this.stopPolling()
      window.location = result.project_url
      return
    }

    if (payload.status === 'invalid' || payload.status === 'error') {
      this.terminalHandled = true
      this.stopPolling()
      this.setProcessing(false)
      this.showStatus(result.error || payload.error_message || 'The import could not be completed.', 'danger')
    }
  }

  setProcessing(processing) {
    this.processingValue = processing
    this.syncProcessingState()
  }

  syncProcessingState() {
    this.element.classList.toggle('is-processing', this.processingValue)
    const busy = this.processingValue || this.supportSaving
    this.confirmButtonTarget.disabled = busy || this.scenarioCount() <= 0
    this.confirmButtonTarget.textContent = this.processingValue ? 'Creating project…' : this.projectEditValue ? 'Done' : 'Create project'
    if (this.hasFilterInputTarget) this.filterInputTarget.disabled = busy
    this.profileRowsTarget.querySelectorAll('input, button').forEach((input) => { input.disabled = busy || (this.projectEditValue && input.type === 'checkbox') })
    this.originBlocksTarget.querySelectorAll('input, button').forEach((input) => { input.disabled = busy || this.projectEditValue })
    this.element.querySelectorAll('.lane-matrix-review__lanes-tools button').forEach(button => { button.disabled = busy || this.projectEditValue })
    if (this.hasSupportEditorTarget) this.supportEditorTarget.querySelectorAll('input, select, button').forEach(input => { input.disabled = busy || (this.projectEditValue && input.dataset.supportField === 'trailer') })
  }

  showStatus(message, type) {
    this.statusTarget.textContent = message
    this.statusTarget.className = `alert alert-${type || 'info'}`
    this.statusTarget.hidden = !message
  }

  setMembership(set, key, present) {
    if (present) set.add(String(key))
    else set.delete(String(key))
  }

  checkbox(label, checked) {
    const input = document.createElement('input')
    input.type = 'checkbox'
    input.checked = checked
    input.setAttribute('aria-label', label)
    input.disabled = this.processingValue
    return input
  }

  inlineInput(value, label) {
    const input = document.createElement('input')
    input.type = 'text'
    input.className = 'lane-matrix-review__inline-input'
    input.value = value == null ? '' : String(value)
    input.setAttribute('aria-label', label)
    input.disabled = this.processingValue
    return input
  }

  elementFor(tag, className, text) {
    const element = document.createElement(tag)
    if (className) element.className = className
    if (text != null) element.textContent = String(text)
    return element
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.getAttribute('content') : ''
  }

  deepClone(value) {
    return JSON.parse(JSON.stringify(value || {}))
  }

  pluralize(count, noun) {
    return `${count.toLocaleString()} ${noun}${count === 1 ? '' : 's'}`
  }
}
